import React, { useCallback, useMemo, useRef } from "react";

function parseValue(value, fallback, rangeMin, rangeMax) {
    if (value === "") return fallback;
    const number = Number(String(value).replace(",", "."));
    return Number.isFinite(number) ? Math.min(rangeMax, Math.max(rangeMin, number)) : fallback;
}

function clampInput(value, rangeMin, rangeMax) {
    if (value === "") return "";
    const number = Number(String(value).replace(",", "."));
    if (!Number.isFinite(number)) return "";
    return String(Math.min(rangeMax, Math.max(rangeMin, number)));
}

function formatInputValue(value) {
    return value === "" ? "" : String(value);
}

function valueToPositionRatio(value, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio) {
    const v = Math.min(rangeMax, Math.max(rangeMin, value));
    if (breakPoint == null || breakPoint <= rangeMin || breakPoint >= rangeMax) {
        return (v - rangeMin) / (rangeMax - rangeMin);
    }
    const seg2Range = rangeMax - breakPoint;
    if (v <= breakPoint) {
        return ((v - rangeMin) / (breakPoint - rangeMin)) * seg1Ratio;
    }
    return seg1Ratio + ((v - breakPoint) / seg2Range) * seg2Ratio;
}

function valueToPositionPct(value, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio) {
    return valueToPositionRatio(value, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio) * 100;
}

function positionRatioToValue(ratio, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio) {
    const p = Math.min(1, Math.max(0, ratio));
    if (breakPoint == null || breakPoint <= rangeMin || breakPoint >= rangeMax) {
        return rangeMin + p * (rangeMax - rangeMin);
    }
    const seg2Range = rangeMax - breakPoint;
    if (p <= seg1Ratio) {
        return rangeMin + (p / seg1Ratio) * (breakPoint - rangeMin);
    }
    return breakPoint + ((p - seg1Ratio) / seg2Ratio) * seg2Range;
}

function snapValue(value, step, rangeMin, rangeMax) {
    const snapped = Math.round(value / step) * step;
    return Math.min(rangeMax, Math.max(rangeMin, snapped));
}

export default function RangeFilter({
    label,
    minValue,
    maxValue,
    onRangeChange,
    rangeMin,
    rangeMax,
    breakPoint = null,
    seg1Ratio = 2 / 3,
    seg2Ratio = 1 / 3,
    step = 1,
    ticks = [],
    unit = "",
    minInputName = "range-min",
    maxInputName = "range-max",
    minAriaLabel = "Минимум",
    maxAriaLabel = "Максимум",
}) {
    const trackRef = useRef(null);
    const minVal = useMemo(
        () => parseValue(minValue, rangeMin, rangeMin, rangeMax),
        [minValue, rangeMin, rangeMax],
    );
    const maxVal = useMemo(
        () => parseValue(maxValue, rangeMax, rangeMin, rangeMax),
        [maxValue, rangeMin, rangeMax],
    );
    const minPct = valueToPositionPct(minVal, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio);
    const maxPct = valueToPositionPct(maxVal, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio);
    const breakPct = breakPoint != null ? valueToPositionPct(breakPoint, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio) : null;

    const emitRange = useCallback((nextMin, nextMax) => {
        const low = Math.min(nextMin, nextMax);
        const high = Math.max(nextMin, nextMax);
        onRangeChange(
            low <= rangeMin ? "" : String(low),
            high >= rangeMax ? "" : String(high),
        );
    }, [onRangeChange, rangeMin, rangeMax]);

    const onMinInputChange = useCallback((event) => {
        const raw = event.target.value.trim();
        if (raw === "") {
            onRangeChange("", maxValue);
            return;
        }
        const clamped = clampInput(raw, rangeMin, rangeMax);
        if (clamped === "") return;
        emitRange(Number(clamped), maxVal);
    }, [emitRange, maxValue, maxVal, onRangeChange, rangeMin, rangeMax]);

    const onMaxInputChange = useCallback((event) => {
        const raw = event.target.value.trim();
        if (raw === "") {
            onRangeChange(minValue, "");
            return;
        }
        const clamped = clampInput(raw, rangeMin, rangeMax);
        if (clamped === "") return;
        emitRange(minVal, Number(clamped));
    }, [emitRange, minValue, minVal, onRangeChange, rangeMin, rangeMax]);

    const onMinInputBlur = useCallback((event) => {
        const clamped = clampInput(event.target.value, rangeMin, rangeMax);
        if (clamped === event.target.value) return;
        emitRange(clamped === "" ? rangeMin : Number(clamped), maxVal);
    }, [emitRange, maxVal, rangeMin, rangeMax]);

    const onMaxInputBlur = useCallback((event) => {
        const clamped = clampInput(event.target.value, rangeMin, rangeMax);
        if (clamped === event.target.value) return;
        emitRange(minVal, clamped === "" ? rangeMax : Number(clamped));
    }, [emitRange, minVal, rangeMin, rangeMax]);

    const dragHandle = useCallback((which, clientX) => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const thumbPad = 10;
        const usable = rect.width - thumbPad * 2;
        const offset = Math.min(usable, Math.max(0, clientX - rect.left - thumbPad));
        const ratio = offset / usable;
        const value = snapValue(positionRatioToValue(ratio, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio), step, rangeMin, rangeMax);
        if (which === "min") emitRange(value, maxVal);
        else emitRange(minVal, value);
    }, [emitRange, maxVal, minVal, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio, step]);

    const onHandlePointerDown = useCallback((which) => (event) => {
        event.preventDefault();
        dragHandle(which, event.clientX);
        const onMove = (moveEvent) => dragHandle(which, moveEvent.clientX);
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    }, [dragHandle]);

    return (
        <div className="filter-field range-filter">
            <div className="fish-multi-filter__head">
                <span className="fish-multi-filter__label">
                    {label}
                    {unit ? <span className="range-filter__unit-inline">{unit}</span> : null}
                </span>
            </div>
            <div className="range-filter__inputs-row">
                <input
                    className="range-filter__price-input range-filter__price-input--min"
                    type="text"
                    inputMode="decimal"
                    name={minInputName}
                    placeholder={String(rangeMin)}
                    value={formatInputValue(minValue)}
                    onChange={onMinInputChange}
                    onBlur={onMinInputBlur}
                    aria-label={minAriaLabel}
                />
                <input
                    className="range-filter__price-input range-filter__price-input--max"
                    type="text"
                    inputMode="decimal"
                    name={maxInputName}
                    placeholder={String(rangeMax)}
                    value={formatInputValue(maxValue)}
                    onChange={onMaxInputChange}
                    onBlur={onMaxInputBlur}
                    aria-label={maxAriaLabel}
                />
            </div>
            <div
                className="range-filter__slider-wrap"
                ref={trackRef}
                style={{ "--range-min-pct": minPct, "--range-max-pct": maxPct }}
            >
                <div className="range-filter__ticks" aria-hidden>
                    {ticks.map((tick) => (
                        <span
                            key={tick}
                            className="range-filter__tick"
                            style={{ left: `${valueToPositionPct(tick, rangeMin, rangeMax, breakPoint, seg1Ratio, seg2Ratio)}%` }}
                        >
                            {tick}
                        </span>
                    ))}
                </div>
                <div className="range-filter__track">
                    <div className="range-filter__rail" aria-hidden />
                    {breakPct != null && (
                        <div className="range-filter__segment-mark" style={{ left: `${breakPct}%` }} aria-hidden />
                    )}
                    <div className="range-filter__fill" aria-hidden />
                    <button
                        type="button"
                        className="range-filter__handle range-filter__handle--min"
                        style={{ left: `${minPct}%` }}
                        onPointerDown={onHandlePointerDown("min")}
                        aria-label={minAriaLabel}
                    />
                    <button
                        type="button"
                        className="range-filter__handle range-filter__handle--max"
                        style={{ left: `${maxPct}%` }}
                        onPointerDown={onHandlePointerDown("max")}
                        aria-label={maxAriaLabel}
                    />
                </div>
            </div>
        </div>
    );
}
